import type {
	IdentityProviderModePayload,
	IdentityProviderPayload,
} from '@Application/Security/Sso/Actions/identityProviderFetched';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `clientSecretRegisteredAt` is converted to a `Date`; `lastTestedAt` stays a `string | null` — the
 * two fields are treated differently on purpose (contract, §7.1). `clientSecret` has no place here:
 * the server never serves it back (SEC-12).
 */
class IdentityProviderView {
	readonly id: number;
	readonly mode: IdentityProviderModePayload;
	readonly isEnabled: boolean;
	readonly issuerUrl: string;
	readonly clientId: string;
	readonly emailDomains: readonly string[];
	readonly clientSecretRegisteredAt: Date;
	readonly lastTestedAt: string | null;
	readonly lastTestSucceeded: boolean | null;
	readonly accountId: number;
	readonly callbackUrl: string;

	constructor(payload: IdentityProviderPayload) {
		this.id = payload.id;
		this.mode = payload.mode;
		this.isEnabled = payload.isEnabled;
		this.issuerUrl = payload.issuerUrl;
		this.clientId = payload.clientId;
		this.emailDomains = payload.emailDomains;
		this.clientSecretRegisteredAt = new Date(payload.clientSecretRegisteredAt);
		this.lastTestedAt = payload.lastTestedAt;
		this.lastTestSucceeded = payload.lastTestSucceeded;
		this.accountId = payload.account.id;
		this.callbackUrl = payload.callbackUrl;

		Object.freeze(this);
	}

	get isExclusive(): boolean {
		return this.mode.value === 'exclusive';
	}

	/** Applies a fresh connection test result, leaving every other field untouched. */
	withConnectionTestResult(succeeded: boolean, testedAt: string): IdentityProviderView {
		return new IdentityProviderView({
			id: this.id,
			mode: this.mode,
			isEnabled: this.isEnabled,
			issuerUrl: this.issuerUrl,
			clientId: this.clientId,
			emailDomains: [...this.emailDomains],
			clientSecretRegisteredAt: this.clientSecretRegisteredAt.toISOString(),
			lastTestedAt: testedAt,
			lastTestSucceeded: succeeded,
			account: { id: this.accountId },
			callbackUrl: this.callbackUrl,
		});
	}
}

export default IdentityProviderView;
